import axios from 'axios';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Shield, RotateCcw, Save, Users, Lock, Layers, ClipboardList, Eye, Search, ChevronDown, AlertTriangle, FileText } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// 🔀 [Phase v13.61 Vague 5c — 2026-08-25] Refonte hiérarchique.
//   Mapping : groupe d'action/BT → clé de page parente. Utilisé pour rassembler
//   toutes les sous-permissions d'une page sous la page elle-même dans l'UI.
//   Convention : "X — Y" (avec em-dash) → parent = "X". Sinon lookup direct par nom.
const GROUPE_TO_PAGE_PARENT_LABEL = {
  // Exceptions et clarifications :
  "Vue d'ensemble": "vue_ensemble",
  "Vue d'ensemble — Alertes": "alertes",
  "Vue d'ensemble — Ventes": "ventes",
  "Vue d'ensemble — Départements": "vue_ensemble",
  "Vue d'ensemble — Widgets": "vue_ensemble",
  "Vue d'ensemble — Équipes actives": "vue_ensemble",
  "Entrepôt": "entrepot",
  "Entrepôt — Étiquettes": "entrepot",
  "Projets": "projets",
  "Lavage": "lavage",
  "Validation des heures": "validation_heures",
  "Rapport de pointage": "rapport_heures",
  "Calendrier": "rendez_vous",
  "Congés": "conges",
  "Bonus": "mon_bonus",
  "Avertissements": "avertissements",
  "Facturation": "lmds_facturation",
  "LMDS": "lmds",
  "LMDS — Profils": "lmds",
  "LMDS — Rapports": "lmds",
  "LMDS — Bonus": "lmds",
  "LMDS — Non payées": "lmds_non_payees",
  "LMDS — Facturation": "lmds_facturation",
  "LMDS — Facturation SC": "lmds_facturation",
  "Unités": "vehicules",
  "Production": "production",
  "Mon Espace": "mon_espace",
  "Mon Espace — Employés": "mon_espace",
  "Dossiers": "clients",
  "Inspection": "inspection",
  "To-do List": "todo_list",
  "Messagerie": "messagerie",
  "Propositions": "propositions",
  "IA": "vue_ensemble",  // le chat IA vit sur le dashboard/sidebar
  "Admin technique": "vue_ensemble",
  "Analyses": "ventes",  // les analyses sont liées à ventes
  "Paie": "validation_heures",
  "Config Employés": "employes",
  // Bon de Travail — toutes les actions BT vont sous la page `projets`
  "Bon de Travail — Liste": "projets",
  "Bon de Travail — Détail": "projets",
  "Bon de Travail — Général": "projets",
  "Bon de Travail — Commandes": "projets",
  "Bon de Travail — Incidents": "projets",
  "Bon de Travail — Actions dynamiques": "projets",
};

// Détecte si une clé est une restriction (owner short-circuit ne l'active pas)
const isRestrictiveKey = (key) => {
  if (!key) return false;
  return key.endsWith('_seulement') || key.startsWith('restrict_');
};

const ROLE_COLORS = {
  proprietaire:  { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
  charge_projet: { bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300' },
  adjoint_admin: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  chef_equipe:   { bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-300' },
  peintre:       { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  installateur:  { bg: 'bg-teal-100',   text: 'text-teal-800',   border: 'border-teal-300' },
  representante: { bg: 'bg-pink-100',   text: 'text-pink-800',   border: 'border-pink-300' },
};

export function GestionAcces() {
  const [meta, setMeta] = useState(null); // pages_definition, actions_definition, employe_tabs_definition, role_labels
  const [allPerms, setAllPerms] = useState({});
  const [selectedRole, setSelectedRole] = useState('charge_projet');
  const [localPerms, setLocalPerms] = useState({ pages: {}, actions: {}, employe_tabs: {}, dossier_onglets: {}, bon_travail_actions: {} });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  // 🆕 [Vague 5c] Filtre de recherche
  const [searchQuery, setSearchQuery] = useState('');

  // 🆕 2026-02-20 — Mode spectateur rapide depuis Gestion des permissions
  const [showImpersonatePicker, setShowImpersonatePicker] = useState(false);
  const [eligibleEmployees, setEligibleEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [imperSearch, setImperSearch] = useState('');
  const { impersonate } = useAuth() || {};
  const navigate = useNavigate();

  // 🆕 2026-05-26 — Modal historique des modifications
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const openAuditLog = async () => {
    setShowAuditLog(true);
    setLoadingAudit(true);
    try {
      const r = await axios.get(`${API_URL}/api/permissions-audit-log?limit=100`, { headers });
      setAuditEntries(r.data?.entries || []);
    } catch (e) {
      toast.error(`Erreur historique : ${e.message}`);
    } finally {
      setLoadingAudit(false);
    }
  };

  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/permissions`, { headers });
      if (r.status !== 200) throw new Error();
      const data = r.data;
      setMeta({
        pages_definition: data.pages_definition,
        actions_definition: data.actions_definition,
        employe_tabs_definition: data.employe_tabs_definition || [],
        dossier_onglets_definition: data.dossier_onglets_definition || [],
        bon_travail_actions_definition: data.bon_travail_actions_definition || [],
        role_labels: data.role_labels,
      });
      setAllPerms(data.permissions);
      setLoading(false);
    } catch {
      toast.error('Impossible de charger les permissions');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allPerms[selectedRole]) {
      setLocalPerms({
        pages:               { ...allPerms[selectedRole].pages },
        actions:             { ...allPerms[selectedRole].actions },
        employe_tabs:        { ...allPerms[selectedRole].employe_tabs },
        dossier_onglets:     { ...allPerms[selectedRole].dossier_onglets },
        bon_travail_actions: { ...allPerms[selectedRole].bon_travail_actions },
      });
      setIsDirty(false);
    }
  }, [selectedRole, allPerms]);

  const togglePage = (key) => {
    setLocalPerms(prev => ({ ...prev, pages: { ...prev.pages, [key]: !prev.pages[key] } }));
    setIsDirty(true);
  };

  const toggleAction = (key) => {
    setLocalPerms(prev => ({ ...prev, actions: { ...prev.actions, [key]: !prev.actions[key] } }));
    setIsDirty(true);
  };

  const toggleTab = (key) => {
    setLocalPerms(prev => ({ ...prev, employe_tabs: { ...prev.employe_tabs, [key]: !prev.employe_tabs[key] } }));
    setIsDirty(true);
  };

  const toggleDossierOnglet = (key) => {
    setLocalPerms(prev => ({ ...prev, dossier_onglets: { ...prev.dossier_onglets, [key]: !prev.dossier_onglets[key] } }));
    setIsDirty(true);
  };

  const toggleBonTravailAction = (key) => {
    setLocalPerms(prev => ({ ...prev, bon_travail_actions: { ...prev.bon_travail_actions, [key]: !prev.bon_travail_actions[key] } }));
    setIsDirty(true);
  };

  // 🆕 2026-02-20 — Ouvre le picker d'employés du rôle sélectionné pour
  //   tester rapidement les permissions en mode spectateur.
  // 🔧 2026-05-26 — Filtre amélioré : matche sur type_employe OU role_terrain
  //   OU role_admin (avant : seulement type_employe, ce qui ratait les chefs
  //   d'équipe avec type_employe="terrain" + role_terrain="chef_equipe").
  const openImpersonatePicker = async () => {
    setImperSearch('');
    setShowImpersonatePicker(true);
    setLoadingEmployees(true);
    try {
      const r = await axios.get(`${API_URL}/api/employees`, { headers });
      const all = Array.isArray(r.data) ? r.data : (r.data?.employees || r.data?.employes || []);
      const sel = (selectedRole || '').toLowerCase();
      const filtered = all.filter(e => {
        if (!e) return false;
        // Filtrer les inactifs (gestion des 2 conventions : est_actif boolean et statut string)
        if (e.est_actif === false) return false;
        if (e.statut === 'inactif') return false;
        // Matche sur N'IMPORTE LEQUEL des champs de rôle
        const t = (e.type_employe || '').toLowerCase();
        const rt = (e.role_terrain || '').toLowerCase();
        const ra = (e.role_admin || '').toLowerCase();
        return t === sel || rt === sel || ra === sel;
      });
      setEligibleEmployees(filtered);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger la liste d'employés");
    } finally {
      setLoadingEmployees(false);
    }
  };

  const doImpersonate = async (employee) => {
    if (!employee?.id) return;
    if (!impersonate) {
      toast.error("Mode spectateur indisponible");
      return;
    }
    try {
      await impersonate(employee.id);
      setShowImpersonatePicker(false);
      toast.success(`Connecté comme ${employee.prenom} ${employee.nom}`);
      navigate('/'); // HomeRedirect dirige vers la bonne page selon le rôle
    } catch (e) {
      toast.error(e.message || 'Échec du mode spectateur');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/permissions/${selectedRole}`, localPerms, { headers });
      if (r.status !== 200) throw new Error((r.data).detail);
      const updated = r.data;
      setAllPerms(prev => ({ ...prev, [selectedRole]: { pages: updated.pages, actions: updated.actions, employe_tabs: updated.employe_tabs, dossier_onglets: updated.dossier_onglets, bon_travail_actions: updated.bon_travail_actions || {} } }));
      setIsDirty(false);
      toast.success('Permissions sauvegardées !');
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Réinitialiser les permissions de "${meta?.role_labels[selectedRole]}" aux valeurs par défaut ?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/permissions/${selectedRole}/reset`, null, { headers });
      if (r.status !== 200) throw new Error();
      const data = r.data;
      setAllPerms(prev => ({ ...prev, [selectedRole]: { pages: data.pages, actions: data.actions, employe_tabs: data.employe_tabs || {}, dossier_onglets: data.dossier_onglets || {}, bon_travail_actions: data.bon_travail_actions || {} } }));
      toast.success('Réinitialisé !');
    } catch {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // 🔀 [Vague 5c] Construction de l'arbre hiérarchique : pageKey → { pageDef, actions[], employe_tabs[], dossier_onglets[], bon_travail_actions[] }
  const hierarchie = useMemo(() => {
    if (!meta) return { rootPages: {}, orphelins: { actions: [], bt: [] } };
    const pageByKey = {};
    (meta.pages_definition || []).forEach(p => {
      pageByKey[p.key] = { pageDef: p, actions: [], bon_travail_actions: [], employe_tabs: [], dossier_onglets: [] };
    });
    const orphelinsActions = [];
    const orphelinsBT = [];
    (meta.actions_definition || []).forEach(a => {
      const parentKey = GROUPE_TO_PAGE_PARENT_LABEL[a.groupe] || null;
      if (parentKey && pageByKey[parentKey]) {
        pageByKey[parentKey].actions.push(a);
      } else {
        orphelinsActions.push(a);
      }
    });
    (meta.bon_travail_actions_definition || []).forEach(a => {
      const parentKey = GROUPE_TO_PAGE_PARENT_LABEL[a.groupe] || 'projets';
      if (pageByKey[parentKey]) {
        pageByKey[parentKey].bon_travail_actions.push(a);
      } else {
        orphelinsBT.push(a);
      }
    });
    if (pageByKey['employes']) {
      pageByKey['employes'].employe_tabs = meta.employe_tabs_definition || [];
    }
    if (pageByKey['clients']) {
      pageByKey['clients'].dossier_onglets = meta.dossier_onglets_definition || [];
    }
    return { rootPages: pageByKey, orphelins: { actions: orphelinsActions, bt: orphelinsBT } };
  }, [meta]);

  const pagesByGroup = useMemo(() => {
    const g = {};
    (meta?.pages_definition || []).forEach(p => {
      if (!g[p.groupe]) g[p.groupe] = [];
      g[p.groupe].push(p);
    });
    return g;
  }, [meta]);

  const filteredMatch = useCallback((str) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (str || '').toLowerCase().includes(q);
  }, [searchQuery]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  const isOwner = selectedRole === 'proprietaire';

  // Compteurs globaux
  const pagesCount = Object.values(localPerms.pages || {}).filter(Boolean).length;
  const actionsCount = Object.values(localPerms.actions || {}).filter(Boolean).length;
  const btCount = Object.values(localPerms.bon_travail_actions || {}).filter(Boolean).length;

  const pageMatchesFilter = (pageBundle) => {
    if (!searchQuery.trim()) return true;
    if (filteredMatch(pageBundle.pageDef.label)) return true;
    if (pageBundle.pageDef.key && filteredMatch(pageBundle.pageDef.key)) return true;
    // Cherche dans les sous-permissions
    if (pageBundle.actions.some(a => filteredMatch(a.description) || filteredMatch(a.key))) return true;
    if (pageBundle.bon_travail_actions.some(a => filteredMatch(a.description) || filteredMatch(a.key))) return true;
    if ((pageBundle.employe_tabs || []).some(t => filteredMatch(t.label) || filteredMatch(t.description) || filteredMatch(t.key))) return true;
    if ((pageBundle.dossier_onglets || []).some(t => filteredMatch(t.label) || filteredMatch(t.description) || filteredMatch(t.key))) return true;
    return false;
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" />
            Gestion des Permissions
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Contrôlez qui voit quoi et qui peut faire quoi dans la plateforme.
            {isDirty && <span className="ml-2 text-amber-600 font-medium">• Modifications non sauvegardées</span>}
          </p>
        </div>
        {!isOwner && (
          <div className="flex gap-2 sm:gap-3">
            <Button variant="outline" onClick={openAuditLog} disabled={saving} title="Voir l'historique des modifications" data-testid="btn-historique-perms">
              <ClipboardList className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Historique</span>
              <span className="sm:hidden">Hist.</span>
            </Button>
            <Button variant="outline" onClick={reset} disabled={saving}>
              <RotateCcw className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Réinitialiser</span>
              <span className="sm:hidden">Réinit.</span>
            </Button>
            <Button onClick={save} disabled={saving} className={`text-white ${isDirty ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        )}
      </div>

      {/* Role selector */}
      <div className="flex flex-wrap items-center gap-3">
        {Object.entries(meta?.role_labels || {}).map(([role, label]) => {
          const colors = ROLE_COLORS[role] || {};
          const active = selectedRole === role;
          return (
            <button
              key={role}
              onClick={() => setSelectedRole(role)}
              data-testid={`role-tab-${role}`}
              className={`px-4 py-2 rounded-lg border-2 font-medium transition-all text-sm flex items-center gap-2
                ${active
                  ? `${colors.bg} ${colors.text} ${colors.border} shadow-sm`
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
            >
              {role === 'proprietaire' /* E3b-whitelist : clé de rôle de la matrice */ && <Lock className="w-3.5 h-3.5" />}
              {role !== 'proprietaire' /* E3b-whitelist : clé de rôle de la matrice */ && <Users className="w-3.5 h-3.5" />}
              {label}
              <span className={`text-xs ml-1 px-1.5 py-0.5 rounded-full ${active ? 'bg-white/60' : 'bg-slate-100'}`}>
                {role === 'proprietaire' /* E3b-whitelist : clé de rôle de la matrice */ ? 'Tout' : (() => {
                  const rp = allPerms[role] || {};
                  const pc = Object.values(rp.pages || {}).filter(Boolean).length;
                  const ac = Object.values(rp.actions || {}).filter(Boolean).length;
                  return `${pc + ac} actifs`;
                })()}
              </span>
            </button>
          );
        })}
        {!isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={openImpersonatePicker}
            data-testid="impersonate-from-permissions-btn"
            className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-50"
            title={`Voir la plateforme comme un ${(meta?.role_labels || {})[selectedRole] || selectedRole}`}
          >
            <Eye className="w-4 h-4 mr-1.5" />
            Tester ce rôle
          </Button>
        )}
      </div>

      {/* Owner notice — verrou anti-lockout */}
      {isOwner && (
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <Lock className="w-5 h-5 text-purple-600" />
            <p className="text-purple-700 text-sm font-medium">
              Le Propriétaire a toujours accès à toutes les pages et toutes les fonctionnalités. Ces permissions sont verrouillées et ne peuvent pas être modifiées (protection anti-lockout côté backend).
            </p>
          </CardContent>
        </Card>
      )}

      {/* 🔀 [Vague 5c] Recherche + compteurs synthèse */}
      {!isOwner && (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Rechercher une permission (nom de page, action, mot-clé)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9 text-sm"
              data-testid="perms-search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Badge className="bg-blue-100 text-blue-700" data-testid="counter-pages">{pagesCount} page(s)</Badge>
            <Badge className="bg-orange-100 text-orange-700" data-testid="counter-actions">{actionsCount} action(s)</Badge>
            <Badge className="bg-violet-100 text-violet-700" data-testid="counter-bt">{btCount} action(s) BT</Badge>
          </div>
        </div>
      )}

      {/* 🔀 [Vague 5c] Vue hiérarchique : par catégorie → par PAGE PARENTE → sous-permissions indentées */}
      {!isOwner && (
        <div className="space-y-6">
          {Object.entries(pagesByGroup).map(([groupe, pagesInGroup]) => {
            // Filtrer les pages du groupe selon la recherche
            const visiblePages = pagesInGroup.filter(p => {
              const bundle = hierarchie.rootPages[p.key];
              if (!bundle) return filteredMatch(p.label) || filteredMatch(p.key);
              return pageMatchesFilter(bundle);
            });
            if (visiblePages.length === 0) return null;

            return (
              <div key={groupe} className="space-y-3">
                <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide px-1">
                  {groupe}
                </h2>
                <div className="space-y-3">
                  {visiblePages.map(page => {
                    const bundle = hierarchie.rootPages[page.key];
                    const pageEnabled = !!localPerms.pages?.[page.key];
                    const hasSubItems = bundle && (
                      bundle.actions.length > 0 ||
                      bundle.bon_travail_actions.length > 0 ||
                      bundle.employe_tabs.length > 0 ||
                      bundle.dossier_onglets.length > 0
                    );
                    const activeSubs = bundle ? (
                      bundle.actions.filter(a => localPerms.actions?.[a.key]).length +
                      bundle.bon_travail_actions.filter(a => localPerms.bon_travail_actions?.[a.key]).length +
                      bundle.employe_tabs.filter(t => localPerms.employe_tabs?.[t.key]).length +
                      bundle.dossier_onglets.filter(t => localPerms.dossier_onglets?.[t.key]).length
                    ) : 0;
                    const totalSubs = bundle ? (
                      bundle.actions.length + bundle.bon_travail_actions.length +
                      bundle.employe_tabs.length + bundle.dossier_onglets.length
                    ) : 0;

                    return (
                      <Card key={page.key} className={`transition-all ${pageEnabled ? 'border-slate-200' : 'border-slate-100 bg-slate-50/50'}`}
                            data-testid={`page-card-${page.key}`}>
                        <CardHeader className="pb-2 pt-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <FileText className={`w-4 h-4 ${pageEnabled ? 'text-blue-600' : 'text-slate-400'}`} />
                                <h3 className={`text-base font-semibold ${pageEnabled ? 'text-slate-900' : 'text-slate-500'}`}>
                                  {page.label}
                                </h3>
                                {hasSubItems && (
                                  <Badge className="bg-slate-100 text-slate-600 text-xs" data-testid={`page-badge-${page.key}`}>
                                    {activeSubs}/{totalSubs} sous-perms
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`text-xs font-medium ${pageEnabled ? 'text-green-700' : 'text-slate-400'}`}>
                                {pageEnabled ? 'Page activée' : 'Page désactivée'}
                              </span>
                              <Switch
                                checked={pageEnabled}
                                onCheckedChange={() => togglePage(page.key)}
                                data-testid={`page-toggle-${page.key}`}
                              />
                            </div>
                          </div>
                        </CardHeader>
                        {hasSubItems && (
                          <CardContent className={`pb-3 pt-1 ${!pageEnabled ? 'opacity-50' : ''}`}>
                            <Accordion type="single" collapsible defaultValue={pageEnabled ? "sub" : undefined} className="border-t border-slate-100 pt-2">
                              <AccordionItem value="sub" className="border-0">
                                <AccordionTrigger className="py-2 hover:no-underline text-xs text-slate-500 uppercase tracking-wide">
                                  <span className="flex items-center gap-2">
                                    <ChevronDown className="w-3.5 h-3.5" />
                                    Sous-permissions ({totalSubs})
                                  </span>
                                </AccordionTrigger>
                                <AccordionContent className="pt-2 space-y-3">
                                  {/* Actions */}
                                  {bundle.actions.length > 0 && (
                                    <div className="space-y-1.5" data-testid={`page-actions-${page.key}`}>
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-orange-700 pl-1">Actions</p>
                                      {bundle.actions.filter(a => !searchQuery || filteredMatch(a.description) || filteredMatch(a.key) || filteredMatch(page.label)).map(action => {
                                        const restrictive = isRestrictiveKey(action.key);
                                        const enabled = !!localPerms.actions?.[action.key];
                                        return (
                                          <div
                                            key={action.key}
                                            className={`ml-3 p-2 rounded-md border transition-colors cursor-pointer
                                              ${enabled
                                                ? (restrictive ? 'bg-amber-50 border-amber-200' : 'bg-orange-50 border-orange-200')
                                                : 'bg-white border-slate-200'}`}
                                            onClick={() => pageEnabled && toggleAction(action.key)}
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                  {restrictive && (
                                                    <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1 py-0" title="Restriction : True = accès LIMITÉ">
                                                      <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />
                                                      Restriction
                                                    </Badge>
                                                  )}
                                                  <p className={`text-xs font-medium ${enabled ? (restrictive ? 'text-amber-800' : 'text-orange-800') : 'text-slate-600'}`}>
                                                    {action.description}
                                                  </p>
                                                </div>
                                                <p className="text-[10px] text-slate-400 mt-0.5">{action.key}</p>
                                              </div>
                                              <Switch
                                                checked={enabled}
                                                onCheckedChange={() => pageEnabled && toggleAction(action.key)}
                                                onClick={e => e.stopPropagation()}
                                                data-testid={`action-toggle-${action.key}`}
                                                disabled={!pageEnabled}
                                                className="shrink-0 mt-0.5 scale-90"
                                              />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {/* BT Actions */}
                                  {bundle.bon_travail_actions.length > 0 && (
                                    <div className="space-y-1.5" data-testid={`page-bt-actions-${page.key}`}>
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-700 pl-1">Actions Bon de Travail</p>
                                      {bundle.bon_travail_actions.filter(a => !searchQuery || filteredMatch(a.description) || filteredMatch(a.key) || filteredMatch(page.label)).map(action => {
                                        const enabled = !!localPerms.bon_travail_actions?.[action.key];
                                        return (
                                          <div
                                            key={action.key}
                                            className={`ml-3 p-2 rounded-md border transition-colors cursor-pointer
                                              ${enabled ? 'bg-violet-50 border-violet-200' : 'bg-white border-slate-200'}`}
                                            onClick={() => pageEnabled && toggleBonTravailAction(action.key)}
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1">
                                                <p className={`text-xs font-medium ${enabled ? 'text-violet-800' : 'text-slate-600'}`}>
                                                  {action.description}
                                                </p>
                                                <p className="text-[10px] text-slate-400 mt-0.5">{action.key}</p>
                                              </div>
                                              <Switch
                                                checked={enabled}
                                                onCheckedChange={() => pageEnabled && toggleBonTravailAction(action.key)}
                                                onClick={e => e.stopPropagation()}
                                                data-testid={`bon-action-toggle-${action.key}`}
                                                disabled={!pageEnabled}
                                                className="shrink-0 mt-0.5 scale-90"
                                              />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {/* Employe tabs */}
                                  {bundle.employe_tabs.length > 0 && (
                                    <div className="space-y-1.5" data-testid={`page-employe-tabs-${page.key}`}>
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 pl-1">Onglets Fiche Employé</p>
                                      {bundle.employe_tabs.filter(t => !searchQuery || filteredMatch(t.label) || filteredMatch(t.description) || filteredMatch(t.key)).map(tab => {
                                        const enabled = !!localPerms.employe_tabs?.[tab.key];
                                        return (
                                          <div
                                            key={tab.key}
                                            className={`ml-3 p-2 rounded-md border transition-colors cursor-pointer
                                              ${enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
                                            onClick={() => pageEnabled && toggleTab(tab.key)}
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1">
                                                <p className={`text-xs font-semibold ${enabled ? 'text-emerald-800' : 'text-slate-600'}`}>{tab.label}</p>
                                                <p className="text-[10px] text-slate-400 mt-0.5">{tab.description}</p>
                                              </div>
                                              <Switch
                                                checked={enabled}
                                                onCheckedChange={() => pageEnabled && toggleTab(tab.key)}
                                                onClick={e => e.stopPropagation()}
                                                data-testid={`employe-tab-toggle-${tab.key}`}
                                                disabled={!pageEnabled}
                                                className="shrink-0 mt-0.5 scale-90"
                                              />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {/* Dossier onglets */}
                                  {bundle.dossier_onglets.length > 0 && (
                                    <div className="space-y-1.5" data-testid={`page-dossier-onglets-${page.key}`}>
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 pl-1">Onglets Dossier Client</p>
                                      {bundle.dossier_onglets.filter(t => !searchQuery || filteredMatch(t.label) || filteredMatch(t.description) || filteredMatch(t.key)).map(tab => {
                                        const enabled = !!localPerms.dossier_onglets?.[tab.key];
                                        return (
                                          <div
                                            key={tab.key}
                                            className={`ml-3 p-2 rounded-md border transition-colors cursor-pointer
                                              ${enabled ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'}`}
                                            onClick={() => pageEnabled && toggleDossierOnglet(tab.key)}
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1">
                                                <p className={`text-xs font-semibold ${enabled ? 'text-blue-800' : 'text-slate-600'}`}>{tab.label}</p>
                                                <p className="text-[10px] text-slate-400 mt-0.5">{tab.description}</p>
                                              </div>
                                              <Switch
                                                checked={enabled}
                                                onCheckedChange={() => pageEnabled && toggleDossierOnglet(tab.key)}
                                                onClick={e => e.stopPropagation()}
                                                data-testid={`dossier-tab-toggle-${tab.key}`}
                                                disabled={!pageEnabled}
                                                className="shrink-0 mt-0.5 scale-90"
                                              />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

    {/* 🆕 2026-02-20 — Dialog Mode spectateur depuis Gestion des permissions */}
    <Dialog open={showImpersonatePicker} onOpenChange={setShowImpersonatePicker}>
      <DialogContent className="max-w-md" data-testid="impersonate-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-amber-600" />
            Tester comme un {(meta?.role_labels || {})[selectedRole] || selectedRole}
          </DialogTitle>
          <DialogDescription>
            Choisissez un employé pour voir la plateforme comme lui. Vous pourrez revenir à votre compte avec la bannière en haut de la page.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={imperSearch}
            onChange={(e) => setImperSearch(e.target.value)}
            data-testid="impersonate-search-input"
            className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1.5">
          {loadingEmployees && <p className="text-sm text-slate-500 text-center py-4">Chargement...</p>}
          {!loadingEmployees && eligibleEmployees.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">
              Aucun employé actif avec ce rôle
            </p>
          )}
          {!loadingEmployees && eligibleEmployees
            .filter(e => {
              if (!imperSearch.trim()) return true;
              const q = imperSearch.toLowerCase();
              return `${e.prenom || ''} ${e.nom || ''} ${e.email || ''}`.toLowerCase().includes(q);
            })
            .map(emp => (
              <button
                key={emp.id}
                onClick={() => doImpersonate(emp)}
                data-testid={`impersonate-emp-${emp.id}`}
                className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-md border border-slate-200 hover:bg-amber-50 hover:border-amber-300 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-semibold text-sm">
                  {(emp.prenom || '?').charAt(0).toUpperCase()}{(emp.nom || '').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-900 truncate">{emp.prenom} {emp.nom}</p>
                  <p className="text-xs text-slate-500 truncate">{emp.email || '(sans email)'}</p>
                </div>
                <Eye className="w-4 h-4 text-amber-500" />
              </button>
            ))
          }
        </div>
      </DialogContent>
    </Dialog>

    {/* 🆕 2026-05-26 — Modal historique des modifications de permissions */}
    <Dialog open={showAuditLog} onOpenChange={setShowAuditLog}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="audit-log-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            Historique des modifications de permissions
          </DialogTitle>
          <DialogDescription>
            Les 100 dernières modifications. Cliquez sur une entrée pour voir le détail.
          </DialogDescription>
        </DialogHeader>
        {loadingAudit ? (
          <div className="py-8 text-center text-slate-500">Chargement...</div>
        ) : auditEntries.length === 0 ? (
          <div className="py-8 text-center text-slate-500">Aucune modification enregistrée pour le moment.</div>
        ) : (
          <div className="space-y-2 mt-3">
            {auditEntries.map((e) => {
              const date = new Date(e.timestamp);
              const dateStr = date.toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'medium' });
              const isMassive = e.massive_change;
              const isReset = e.action === 'reset';
              return (
                <details
                  key={e.id}
                  className={`rounded-lg border p-3 text-sm ${isMassive ? 'border-amber-400 bg-amber-50' : isReset ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}
                  data-testid={`audit-entry-${e.id}`}
                >
                  <summary className="cursor-pointer flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold ${isReset ? 'bg-red-200 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                        {e.action}
                      </span>
                      <span className="font-medium text-slate-800">{e.role}</span>
                      <span className="text-slate-500 text-xs">par {e.user_email || '?'}</span>
                      {isMassive && (
                        <span className="text-[10px] bg-amber-200 text-amber-900 border border-amber-400 rounded px-1.5 py-0.5 font-semibold">
                          ⚠️ ÉCRASEMENT MASSIF
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {e.total_changes != null && (
                        <span className="text-xs text-slate-600">{e.total_changes} changement(s)</span>
                      )}
                      <span className="text-xs text-slate-400">{dateStr}</span>
                    </div>
                  </summary>
                  {e.diff && (
                    <div className="mt-3 pt-3 border-t border-slate-200 space-y-2 text-xs">
                      {Object.entries(e.diff).map(([cat, changes]) => {
                        const entries = Object.entries(changes || {});
                        if (entries.length === 0) return null;
                        return (
                          <div key={cat}>
                            <p className="font-semibold text-slate-700 mb-1">{cat} ({entries.length}) :</p>
                            <ul className="ml-4 space-y-0.5">
                              {entries.slice(0, 20).map(([k, v]) => (
                                <li key={k} className="text-slate-600">
                                  <code className="bg-slate-100 px-1 rounded">{k}</code> : <span className="text-red-600">{String(v.before)}</span> → <span className="text-green-600">{String(v.after)}</span>
                                </li>
                              ))}
                              {entries.length > 20 && <li className="text-slate-400 italic">... et {entries.length - 20} de plus</li>}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {e.payload_size && (
                    <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] text-slate-500">
                      Payload : {e.payload_size.pages || 0} pages, {e.payload_size.actions || 0} actions, {e.payload_size.dossier_onglets || 0} onglets, {e.payload_size.bon_travail_actions || 0} BT actions
                    </div>
                  )}
                </details>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  </div>
  );
}
